import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArticleCardHero, ArticleCard, ArticleCardLive } from '../components/ArticleCard';
import AdBlock from '../components/AdBlock';
import PartnerBlock from '../components/ads/PartnerBlock';
import InfoFlowSocialShowcase from '../components/social/InfoFlowSocialShowcase';
import MarketsPulseWidget from '../components/MarketsPulseWidget';
import HomeEventsBlock from '../components/HomeEventsBlock';
import ZapChapeauLauncher from '../components/ZapChapeau/ZapChapeauLauncher';
import PenseeDuJourWidget from '../components/Pensee/PenseeDuJourWidget';
import { useContentControls, isSectionVisible } from '../hooks/useContentControls';
import { fetchCategories, fetchPageConfig } from '../hooks/useSiteData';
import useSocialFlags from '../hooks/useSocialFlags';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { ArrowRight, TrendingUp, Clock, Radio, ChevronRight, Video, Pencil, Check, X, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Extraction ID YouTube ──
function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/)|youtu\.be\/)([\w-]+)/);
  return match ? match[1] : null;
}

// ── Widget vidéo sidebar ──
const TYPE_OPTIONS = [
  { value: 'youtube',        label: 'YouTube (lien vidéo ou live)', icon: '📺' },
  { value: 'stream_url',     label: 'URL de flux (HLS/M3U8/MP4)',  icon: '📡' },
  { value: 'iframe_embed',   label: 'Lien streaming (iframe)',     icon: '🔗' },
  { value: 'uploaded_video', label: 'Vidéo uploadée sur le VPS',   icon: '📁' },
  { value: 'embed_code',     label: 'Code embed / Widget HTML',    icon: '🧩' },
];

const BADGE_OPTIONS = [
  { value: 'live',   label: '● LIVE',   color: '#D62828' },
  { value: 'replay', label: '▶ REPLAY', color: '#6366f1' },
  { value: 'none',   label: 'Aucun',    color: '#9ca3af' },
];

const EMBED_TEMPLATES = [
  {
    label: '▶ YouTube Live',
    code: '<iframe width="100%" height="100%" src="https://www.youtube.com/embed/REMPLACER_ID?autoplay=1&rel=0" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>'
  },
  {
    label: '🟣 Twitch',
    code: '<iframe src="https://player.twitch.tv/?channel=REMPLACER_CHAINE&parent=infoflow.studio" frameborder="0" allowfullscreen width="100%" height="100%"></iframe>'
  },
  {
    label: '🔵 Dailymotion',
    code: '<iframe width="100%" height="100%" src="https://www.dailymotion.com/embed/video/REMPLACER_ID?autoplay=1" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>'
  },
  {
    label: '🔴 Restream',
    code: '<iframe src="https://restream.io/embed/REMPLACER_ID" width="100%" height="100%" frameborder="0" allowfullscreen></iframe>'
  },
  {
    label: '📘 Facebook Live',
    code: '<iframe src="https://www.facebook.com/plugins/video.php?href=https%3A%2F%2Fwww.facebook.com%2FPAGE%2Fvideos%2FID&show_text=false" width="100%" height="100%" frameborder="0" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture" allowfullscreen></iframe>'
  },
];

/** Extracts src and allow attributes from an iframe HTML string securely */
function parseEmbedCode(code) {
  if (!code) return { src: null, allow: null };
  const srcMatch = code.match(/src=["']([^"']+)["']/);
  const allowMatch = code.match(/allow=["']([^"']+)["']/);
  return {
    src: srcMatch ? srcMatch[1] : null,
    allow: allowMatch ? allowMatch[1] : 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
  };
}

/** Ensures YouTube embed URLs have enablejsapi, autoplay and muted-autoplay params
 *  so any new channel added later autoplays reliably across browsers. */
function withYtApi(src) {
  if (!src || !src.includes('youtube.com/embed/')) return src;
  let out = src;
  const addFlag = (param, value) => {
    // ne duplique pas si le parametre existe deja (quelle que soit la valeur)
    const re = new RegExp(`[?&]${param}=`);
    if (re.test(out)) return;
    out += (out.includes('?') ? '&' : '?') + `${param}=${value}`;
  };
  addFlag('enablejsapi', 1);
  addFlag('autoplay', 1);
  addFlag('mute', 1); // muted autoplay is always allowed; postMessage unMute restores audio
  addFlag('playsinline', 1);
  addFlag('controls', 0); // masque les controles natifs YouTube (gros bouton play sur iOS)
  addFlag('modestbranding', 1);
  addFlag('rel', 0);
  return out;
}

/** Sends a command to a YouTube iframe via postMessage */
function ytCommand(iframe, func, args = []) {
  if (!iframe?.contentWindow) return;
  iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args }), '*');
}

/** Initializes the YouTube postMessage API handshake */
function ytListen(iframe) {
  if (!iframe?.contentWindow) return;
  iframe.contentWindow.postMessage(JSON.stringify({ event: 'listening' }), '*');
}

/** Renders an iframe player from extracted src — single embed case.
 *  Exposes a visible speaker button to toggle audio on YouTube embeds. */
function EmbedPlayer({ source, title }) {
  const { src, allow } = parseEmbedCode(source);
  const iframeRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const mutedRef = useRef(true);
  const isYt = src && src.includes('youtube.com/embed/');

  const handleLoad = () => {
    if (!isYt || !iframeRef.current) return;
    ytListen(iframeRef.current);
    setTimeout(() => {
      ytCommand(iframeRef.current, 'setVolume', [100]);
      ytCommand(iframeRef.current, mutedRef.current ? 'mute' : 'unMute', []);
      ytCommand(iframeRef.current, 'playVideo', []);
    }, 500);
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    mutedRef.current = next;
    if (!iframeRef.current) return;
    ytCommand(iframeRef.current, 'setVolume', [100]);
    ytCommand(iframeRef.current, next ? 'mute' : 'unMute', []);
  };

  if (!src) {
    return (
      <div className="w-full aspect-video bg-gray-900 flex items-center justify-center">
        <p className="text-amber-400 text-xs text-center px-4">⚠️ Code embed invalide. Vérifiez le code dans l'admin.</p>
      </div>
    );
  }
  return (
    <div className="relative w-full aspect-video">
      <iframe
        ref={iframeRef}
        key={src}
        src={withYtApi(src)}
        className="w-full h-full"
        style={{ border: 'none' }}
        allow={allow}
        allowFullScreen
        loading="lazy"
        scrolling="no"
        title={title || 'Live Stream'}
        onLoad={handleLoad}
      />
      {isYt && (
        <button
          onClick={toggleMute}
          className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center text-sm hover:bg-black/80 transition-colors"
          title={muted ? 'Activer le son' : 'Couper le son'}
        >{muted ? '🔇' : '🔊'}</button>
      )}
    </div>
  );
}

/** Carousel for multiple embed codes with arrow navigation */
function EmbedCarousel({ embeds }) {
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const iframeRef = useRef(null);
  const mutedRef = useRef(true);
  const total = embeds.length;

  const current = embeds[idx] || '';
  const { src, allow } = parseEmbedCode(current);
  const isYt = src && src.includes('youtube.com/embed/');

  const handleLoad = () => {
    if (!isYt || !iframeRef.current) return;
    ytListen(iframeRef.current);
    setTimeout(() => {
      ytCommand(iframeRef.current, 'setVolume', [100]);
      ytCommand(iframeRef.current, mutedRef.current ? 'mute' : 'unMute', []);
      ytCommand(iframeRef.current, 'playVideo', []);
    }, 500);
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    mutedRef.current = next;
    if (!iframeRef.current) return;
    ytCommand(iframeRef.current, 'setVolume', [100]);
    ytCommand(iframeRef.current, next ? 'mute' : 'unMute', []);
  };

  return (
    <div className="relative w-full aspect-video">
      {src ? (
        <iframe
          ref={iframeRef}
          key={`${idx}-${src}`}
          src={withYtApi(src)}
          className="w-full h-full"
          style={{ border: 'none' }}
          allow={allow}
          allowFullScreen
          title={`Source ${idx + 1}`}
          onLoad={handleLoad}
        />
      ) : (
        <div className="w-full h-full bg-gray-900 flex items-center justify-center">
          <p className="text-amber-400 text-xs">⚠️ Code embed invalide</p>
        </div>
      )}

      {isYt && (
        <button
          onClick={toggleMute}
          className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center text-sm hover:bg-black/80 transition-colors"
          title={muted ? 'Activer le son' : 'Couper le son'}
        >{muted ? '🔇' : '🔊'}</button>
      )}

      {total > 1 && (
        <>
          <button
            onClick={() => setIdx(i => Math.max(0, i - 1))}
            disabled={idx === 0}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center text-base font-bold disabled:opacity-30 hover:bg-black/70 transition-opacity"
          >←</button>
          <button
            onClick={() => setIdx(i => Math.min(total - 1, i + 1))}
            disabled={idx === total - 1}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center text-base font-bold disabled:opacity-30 hover:bg-black/70 transition-opacity"
          >→</button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 hidden md:flex gap-1.5">
            {embeds.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full border-none transition-all ${
                  i === idx ? 'w-4 bg-white' : 'w-1.5 bg-white/50'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Extract YouTube ID from embed code or URL */
function extractYoutubeId(input) {
  if (!input) return null;
  const match = input.match(
    /(?:youtube\.com\/(?:embed\/|watch\?v=|live\/|shorts\/)|youtu\.be\/)([\w-]{11})/
  );
  return match ? match[1] : null;
}

const VideoWidget = ({ video, isAdmin, token, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [config, setConfig] = useState({
    type: video?.type || 'youtube',
    source: video?.source || '',
    url: video?.url || '',
    title: video?.title || '',
    badge: video?.badge || 'live',
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [autoLive, setAutoLive] = useState(null);
  const fileRef = useRef(null);

  // Auto-detect YouTube live status
  useEffect(() => {
    const src = video?.source || video?.url || '';
    let ids = [];
    // embed_code: peut être un JSON array
    if (video?.type === 'embed_code' && src) {
      try {
        const parsed = JSON.parse(src);
        ids = (Array.isArray(parsed) ? parsed : [src]).map(extractYoutubeId).filter(Boolean);
      } catch { ids = [extractYoutubeId(src)].filter(Boolean); }
    } else {
      const id = extractYoutubeId(src);
      if (id) ids = [id];
    }
    if (!ids.length) { setAutoLive(null); return; }
    fetch(`${API_URL}/api/youtube/live-status?video_ids=${ids.join(',')}`)
      .then(r => r.json())
      .then(data => {
        const anyLive = Object.values(data).some(v => v.live);
        setAutoLive(anyLive);
      })
      .catch(() => setAutoLive(null));
  }, [video?.source, video?.url, video?.type]);

  // Multi-embed state
  const [embedList, setEmbedList] = useState(() => {
    if (video?.type === 'embed_code' && video?.source) {
      try {
        const parsed = JSON.parse(video.source);
        if (Array.isArray(parsed)) return parsed;
      } catch {}
      return [video.source];
    }
    return [''];
  });
  const [embedIndex, setEmbedIndex] = useState(0);

  // Sync embedList when video prop changes
  useEffect(() => {
    if (video?.type === 'embed_code' && video?.source) {
      try {
        const parsed = JSON.parse(video.source);
        if (Array.isArray(parsed)) { setEmbedList(parsed); return; }
      } catch {}
      setEmbedList([video.source]);
    }
  }, [video?.type, video?.source]);

  const update = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  const saveVideo = async (payload) => {
    try {
      await axios.patch(`${API_URL}/api/page-config`, {
        page: 'home',
        featured_video: { ...payload, updated_at: new Date().toISOString() },
      }, { headers: { Authorization: `Bearer ${token}` } });
      onUpdate(payload);
      setEditing(false);
      setShowPreview(false);
      toast.success('Vidéo mise à jour');
    } catch { toast.error('Erreur de sauvegarde'); }
  };

  const handleSave = () => saveVideo(config);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) {
      toast.error('Le fichier dépasse 500 Mo');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/media/upload-video`, form, {
        headers: { Authorization: `Bearer ${token}` },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      const uploadedUrl = res.data.url;
      update('source', uploadedUrl);
      toast.success('Vidéo uploadée');
      await saveVideo({ ...config, type: 'uploaded_video', source: uploadedUrl, title: config.title || file.name.replace(/\.[^.]+$/, '') });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload vidéo');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Rendu du player (public ou aperçu)
  const renderPlayer = (v) => {
    const vType = v?.type;
    const vSource = v?.source || '';
    const vUrl = v?.url || '';
    const vTitle = v?.title || 'Vidéo';

    // embed_code : support multi-embed avec carousel
    if (vType === 'embed_code' && vSource) {
      let embeds = [];
      try {
        const parsed = JSON.parse(vSource);
        embeds = Array.isArray(parsed) ? parsed : [vSource];
      } catch {
        embeds = [vSource];
      }
      embeds = embeds.filter(e => e && e.trim());
      if (embeds.length === 0) return null;
      if (embeds.length === 1) return <EmbedPlayer source={embeds[0]} title={vTitle} />;
      return <EmbedCarousel embeds={embeds} />;
    }

    // iframe_embed : le source est directement une URL à mettre dans un iframe
    if (vType === 'iframe_embed' && vSource) {
      return (
        <iframe
          src={vSource}
          className="w-full aspect-video"
          style={{ border: 'none' }}
          allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          title={vTitle}
        />
      );
    }

    // stream_url : HLS/M3U8/MP4 direct
    if (vType === 'stream_url' && vSource) {
      return <video src={vSource} controls autoPlay muted className="w-full aspect-video bg-black" />;
    }

    // uploaded_video
    if (vType === 'uploaded_video' && vSource) {
      return <video src={vSource} controls className="w-full aspect-video bg-black" />;
    }

    // youtube (par défaut) — essaie source puis url
    const ytUrl = vSource || vUrl;
    const ytId = getYouTubeId(ytUrl);
    if (ytId) {
      return (
        <iframe
          src={`https://www.youtube.com/embed/${ytId}?rel=0`}
          className="w-full aspect-video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={vTitle}
        />
      );
    }

    // Fallback URL directe
    if (ytUrl) {
      return <video src={ytUrl} controls className="w-full aspect-video bg-black" />;
    }

    // Aucune vidéo
    return (
      <div className="w-full aspect-video bg-gray-900 flex items-center justify-center">
        {isAdmin ? (
          <button onClick={() => setEditing(true)} className="text-gray-400 hover:text-white text-sm flex flex-col items-center gap-2">
            <Video className="w-8 h-8" />
            <span>Ajouter une vidéo</span>
          </button>
        ) : (
          <span className="text-gray-500 text-sm">Aucune vidéo pour le moment</span>
        )}
      </div>
    );
  };

  const videoTitle = config.title || video?.title || 'Vidéo à la une';
  const activeBadge = BADGE_OPTIONS.find(b => b.value === (video?.badge || 'live'));

  if (editing) {
    return (
      <div className="bg-card border border-border rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm">Configurer la vidéo</h3>
          <div className="flex gap-1">
            <button onClick={() => setShowPreview(!showPreview)}
              className="p-1 rounded bg-blue-600 text-white hover:bg-blue-500" title="Aperçu">
              {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
            <button onClick={handleSave} className="p-1 rounded bg-green-600 text-white hover:bg-green-500" title="Sauvegarder">
              <Check className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => { setEditing(false); setShowPreview(false); }} className="p-1 rounded bg-gray-600 text-white hover:bg-gray-500" title="Annuler">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {/* Titre */}
          <input value={config.title} onChange={e => update('title', e.target.value)}
            placeholder="Titre de la vidéo"
            className="w-full px-2 py-1.5 border rounded text-sm" />

          {/* Sélecteur de type */}
          <div>
            <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide mb-1 block">
              Type de source
            </label>
            <div className="grid grid-cols-1 gap-1">
              {TYPE_OPTIONS.map(opt => (
                <button key={opt.value} onClick={() => update('type', opt.value)}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs text-left transition-all ${
                    config.type === opt.value
                      ? 'bg-[#D62828]/10 text-[#D62828] border-2 border-[#D62828] font-bold'
                      : 'bg-muted/50 text-muted-foreground border-2 border-transparent hover:border-border'
                  }`}>
                  <span>{opt.icon}</span>
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Champ source selon le type */}
          {config.type === 'youtube' && (
            <input value={config.source} onChange={e => update('source', e.target.value)}
              placeholder="URL YouTube (vidéo, live, ou lien de partage)"
              className="w-full px-2 py-1.5 border rounded text-sm" />
          )}

          {config.type === 'stream_url' && (
            <input value={config.source} onChange={e => update('source', e.target.value)}
              placeholder="URL du flux (https://...m3u8 ou .mp4)"
              className="w-full px-2 py-1.5 border rounded text-sm font-mono text-xs" />
          )}

          {config.type === 'iframe_embed' && (
            <input value={config.source} onChange={e => update('source', e.target.value)}
              placeholder="URL à intégrer en iframe (ex: https://player.twitch.tv/...)"
              className="w-full px-2 py-1.5 border rounded text-sm font-mono text-xs" />
          )}

          {config.type === 'uploaded_video' && (
            <>
              <input ref={fileRef} type="file" accept="video/mp4,video/webm,video/quicktime,video/*"
                onChange={handleUpload} className="hidden" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading}
                className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-[#D62828]/40 rounded-lg hover:bg-[#D62828]/5 transition-colors text-sm font-medium text-[#D62828] disabled:opacity-50">
                <Video className="w-4 h-4" />
                {uploading ? 'Envoi en cours...' : 'Uploader depuis cet appareil'}
              </button>
              {uploading && (
                <div className="space-y-1">
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-[#D62828] rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <p className="text-xs text-center text-muted-foreground">{uploadProgress}%</p>
                </div>
              )}
              {config.source && (
                <p className="text-[10px] text-muted-foreground truncate">Fichier : {config.source}</p>
              )}
            </>
          )}

          {config.type === 'embed_code' && (() => {
            const total = embedList.length;
            const current = embedList[embedIndex] || '';
            const isFirst = embedIndex === 0;
            const isLast = embedIndex === total - 1;

            const updateCurrent = (val) => {
              const next = [...embedList];
              next[embedIndex] = val;
              setEmbedList(next);
              update('source', JSON.stringify(next));
            };

            const addEmbed = () => {
              const next = [...embedList, ''];
              setEmbedList(next);
              setEmbedIndex(next.length - 1);
              update('source', JSON.stringify(next));
            };

            const removeEmbed = () => {
              if (total === 1) { updateCurrent(''); return; }
              const next = embedList.filter((_, i) => i !== embedIndex);
              setEmbedList(next);
              const newIdx = Math.max(0, embedIndex - 1);
              setEmbedIndex(newIdx);
              update('source', JSON.stringify(next));
            };

            return (
              <div className="space-y-2">
                {/* Navigation bar */}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground">
                    Code embed {embedIndex + 1} / {total}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setEmbedIndex(i => Math.max(0, i - 1))} disabled={isFirst}
                      className={`w-7 h-7 rounded-lg border text-sm font-bold flex items-center justify-center ${isFirst ? 'border-border text-muted-foreground/30 cursor-not-allowed' : 'border-border text-foreground hover:bg-muted cursor-pointer'}`}>←</button>
                    <div className="flex gap-1">
                      {embedList.map((_, i) => (
                        <button key={i} onClick={() => setEmbedIndex(i)}
                          className={`h-1.5 rounded-full transition-all ${i === embedIndex ? 'w-4 bg-[#D62828]' : 'w-1.5 bg-muted-foreground/30'}`} />
                      ))}
                    </div>
                    <button onClick={() => setEmbedIndex(i => Math.min(total - 1, i + 1))} disabled={isLast}
                      className={`w-7 h-7 rounded-lg border text-sm font-bold flex items-center justify-center ${isLast ? 'border-border text-muted-foreground/30 cursor-not-allowed' : 'border-border text-foreground hover:bg-muted cursor-pointer'}`}>→</button>
                    <button onClick={addEmbed}
                      className="px-2 h-7 rounded-lg border border-violet-600 bg-violet-50 text-violet-700 text-[10px] font-bold hover:bg-violet-100">+ Ajouter</button>
                    {total > 1 && (
                      <button onClick={removeEmbed}
                        className="w-7 h-7 rounded-lg border border-red-200 bg-red-50 text-red-500 text-xs flex items-center justify-center hover:bg-red-100">✕</button>
                    )}
                  </div>
                </div>

                {/* Textarea */}
                <textarea
                  value={current}
                  onChange={e => updateCurrent(e.target.value)}
                  placeholder={'Collez ici le code embed complet, ex:\n\n<iframe width="560" height="315"\n  src="https://www.youtube.com/embed/ID"\n  allowfullscreen>\n</iframe>'}
                  rows={6}
                  className="w-full px-2 py-1.5 rounded text-xs font-mono bg-slate-900 text-cyan-300 resize-y border border-slate-700 focus:border-cyan-500 focus:outline-none"
                />

                {/* Counter */}
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>{current.length} caractères</span>
                  <span>{total} source{total > 1 ? 's' : ''} configurée{total > 1 ? 's' : ''}</span>
                </div>

                {/* Templates */}
                <p className="text-[10px] text-muted-foreground font-medium">Templates rapides :</p>
                <div className="flex flex-wrap gap-1">
                  {EMBED_TEMPLATES.map(t => (
                    <button key={t.label} onClick={() => updateCurrent(t.code)}
                      className="px-2 py-0.5 rounded-full border border-indigo-700 bg-indigo-950 text-indigo-300 text-[10px] hover:bg-indigo-900 transition-colors">
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Badge */}
          <div>
            <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide mb-1 block">
              Badge affiché
            </label>
            <div className="flex gap-1.5">
              {BADGE_OPTIONS.map(opt => (
                <button key={opt.value} onClick={() => update('badge', opt.value)}
                  className={`flex-1 py-1 rounded-lg text-xs font-bold transition-all ${
                    config.badge === opt.value
                      ? 'border-2 text-white'
                      : 'border-2 border-transparent bg-muted text-muted-foreground hover:border-border'
                  }`}
                  style={config.badge === opt.value ? { borderColor: opt.color, backgroundColor: opt.color } : {}}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Aperçu */}
          {showPreview && (
            <div className="border border-dashed border-blue-400 rounded-lg overflow-hidden">
              <p className="text-[10px] text-blue-400 font-bold px-2 py-1 bg-blue-950">👁 Aperçu</p>
              {renderPlayer(config)}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden mb-4 relative group">
      {isAdmin && (
        <button onClick={() => setEditing(true)}
          className="absolute top-2 right-2 z-10 p-1.5 rounded-lg bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">
          <Pencil className="w-3.5 h-3.5" />
        </button>
      )}

      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-slate-100">
        <Video className="h-4 w-4 text-[#D62828]" />
        <span className="font-serif font-bold text-sm flex-1">{videoTitle}</span>
        {autoLive === true ? (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white animate-pulse bg-[#D62828]">
            ● EN DIRECT
          </span>
        ) : autoLive === false ? (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white bg-gray-400">
            ● REDIFFUSION
          </span>
        ) : activeBadge && activeBadge.value !== 'none' ? (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white animate-pulse"
            style={{ backgroundColor: activeBadge.color }}>
            {activeBadge.label}
          </span>
        ) : null}
      </div>

      {renderPlayer(video)}
    </div>
  );
};

const HOME_CACHE_KEY = 'infoflow_home_cache_v1';

const _loadHomeCache = () => {
  try {
    const raw = localStorage.getItem(HOME_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Cache valable 30 minutes (sinon on accepte de re-skeleton-er)
    if (Date.now() - (parsed.ts || 0) > 30 * 60 * 1000) return null;
    return parsed;
  } catch { return null; }
};

const _saveHomeCache = (data) => {
  try {
    localStorage.setItem(HOME_CACHE_KEY, JSON.stringify({ ...data, ts: Date.now() }));
  } catch { /* quota or disabled */ }
};

const HomePage = () => {
  const { isAdmin, token } = useAuth();
  const cc = useContentControls();
  const { socialEnabled } = useSocialFlags();
  // Hydrate depuis le cache local pour eviter la page blanche.
  // Lu une seule fois, au montage : ce JSON.parse porte sur ~300 Ko et se
  // rejouait a chaque rendu du composant.
  const [_cached] = useState(_loadHomeCache);
  const [articles, setArticles] = useState(_cached?.articles || []);
  const [categories, setCategories] = useState(_cached?.categories || []);
  const [loading, setLoading] = useState(!_cached);
  const [pensee, setPensee] = useState(null);
  const [penseeLoaded, setPenseeLoaded] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [featuredVideo, setFeaturedVideo] = useState(_cached?.featuredVideo || null);
  const [zapCfg, setZapCfg] = useState(_cached?.zapCfg || null);

  useEffect(() => {
    document.title = "InfoFlow — Comprendre le monde, autrement";
    return () => { document.title = 'InfoFlow'; };
  }, []);

  useEffect(() => {
    // Determine whether the optional card exists before painting the content
    // below it. Removing its loading placeholder after a 404/503 moved the
    // hero on mobile; its final height also depends on the quote text.
    let alive = true;
    axios.get(`${API_URL}/api/quotes/today`, { timeout: 2500 })
      .then(({ data }) => { if (alive && data?.text && data?.author) setPensee(data); })
      .catch(() => {})
      .finally(() => { if (alive) setPenseeLoaded(true); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Trois appels au lieu de six. `POST /api/seed` repondait 403 en
        // production et etait attendu avant toute donnee ; `featured=true` etait
        // le prefixe exact de la premiere liste, le tri placant deja les
        // articles a la une en tete ; rubriques et page-config sont desormais
        // partagees avec le Header et Zap Chapeau. La vue « cartes » ne
        // transporte plus le corps des articles, jamais affiche ici.
        const [articlesRes, categoriesRes, configRes] = await Promise.allSettled([
          axios.get(`${API_URL}/api/articles/cards?limit=20`),
          fetchCategories(),
          fetchPageConfig(),
        ]);

        if (articlesRes.status === 'fulfilled') {
          setArticles(articlesRes.value.data || []);
        }
        let cats = [];
        let fv = null;
        let zc = null;
        if (categoriesRes.status === 'fulfilled') { cats = categoriesRes.value || []; setCategories(cats); }
        if (configRes.status === 'fulfilled') {
          if (configRes.value?.featured_video) {
            fv = configRes.value.featured_video;
            setFeaturedVideo(fv);
          }
          zc = configRes.value?.zap_chapeau || null;
          setZapCfg(zc);
        }
        // Persiste pour eviter la page blanche au prochain reload
        try {
          const articlesToCache = (articlesRes.status === 'fulfilled' ? (articlesRes.value.data || []) : []).slice(0, 24);
          _saveHomeCache({ articles: articlesToCache, categories: cats, featuredVideo: fv, zapCfg: zc });
        } catch { /* ignore */ }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Ces listes se recalculaient a chaque rendu, y compris quand seule la
  // typographie venait de changer. Elles ne dependent que des articles.
  const { heroArticle, sideArticles, latestArticles, liveArticles } = useMemo(() => {
    const featuredArticles = articles.filter(a => a.is_featured);
    const hero = featuredArticles[0] || articles[0];
    // Toujours 3 cartes sous le hero — compléter avec des articles récents si pas assez de featured
    const heroId = hero?.id;
    const featuredSide = featuredArticles.filter(a => a.id !== heroId).slice(0, 3);
    const sideIds = new Set([heroId, ...featuredSide.map(a => a.id)]);
    const unfeatured = articles.filter(a => !a.is_featured && !sideIds.has(a.id));
    const fillCount = 3 - featuredSide.length;
    const fillArticles = fillCount > 0 ? unfeatured.slice(0, fillCount) : [];
    const side = [...featuredSide, ...fillArticles].slice(0, 3);
    // Section "Dernières actualités" : articles non-featured hors hero/side.
    // Fallback : si tous les articles sont marqués featured (cas prod actuel), on
    // remplit la grille avec les articles restants plutôt que d'afficher une
    // section vide alors que la hero n'en consomme que 4.
    const sideIdsFull = new Set([heroId, ...side.map(a => a.id)]);
    return {
      heroArticle: hero,
      sideArticles: side,
      latestArticles: unfeatured.length > 0 ? unfeatured : articles.filter(a => !sideIdsFull.has(a.id)),
      liveArticles: articles.slice(0, 3), // En direct compacte pour laisser Zap Chapeau remonter
    };
  }, [articles]);

  // Placement Zap Chapeau (éditorial) piloté par l'admin via page-config.
  // zapCfg null (config absente) => comportement historique : sidebar.
  const zapEnabled = zapCfg ? zapCfg.enabled !== false : true;
  const zapPosition = zapCfg?.position || 'normal';
  const zapVisible = zapEnabled && zapPosition !== 'hidden';
  const zapInHero = zapVisible && (!!zapCfg?.featured || zapPosition === 'hero');
  const zapAfterFeatured = zapVisible && !zapInHero && zapPosition === 'after_featured';
  const zapInSidebar = zapVisible && !zapInHero && !zapAfterFeatured;

  if (loading || !penseeLoaded) {
    return (
      <div className="container mx-auto px-4 py-12">
        {/* Header rassurant : logo + message + loader */}
        <div className="flex flex-col items-center justify-center mb-10 text-center">
          <img
            src="/brand/infoflow-logo.svg"
            alt="InfoFlow"
            className="h-14 sm:h-16 mb-4 opacity-90"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <p className="text-sm sm:text-base text-muted-foreground font-serif italic">
            Chargement de l'actualité…
          </p>
          <div className="mt-3 flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 bg-[#D62828] rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
            <span className="h-1.5 w-1.5 bg-[#D62828] rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
            <span className="h-1.5 w-1.5 bg-[#D62828] rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
        {/* Skeletons proches du vrai layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 space-y-4">
            <Skeleton className="aspect-[16/9] w-full rounded-xl" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="aspect-[4/3] w-full rounded-lg" />
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 space-y-4">
            <Skeleton className="aspect-video w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
            <Skeleton className="h-72 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section avec En Direct */}
      <section className="border-b border-border">
        <div className="container mx-auto px-4 py-6">
          {/* Zap Chapeau à la une — bloc éditorial premium pleine largeur */}
          {zapInHero && (
            <div className="mb-6" data-testid="zap-featured">
              <ZapChapeauLauncher articles={articles} categories={categories} />
            </div>
          )}

          {/* Pensée du jour — juste après Zap Chapeau, avant les rubriques
              secondaires. Assez visible pour être lue, jamais en concurrence
              avec la Une. */}
          {pensee && (
            <div className="mb-6">
              <PenseeDuJourWidget quote={pensee} />
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Main Hero */}
            <div className="lg:col-span-8">
              {heroArticle && <ArticleCardHero article={heroArticle} />}
              
              {/* Featured Articles Grid — toujours 3 cartes */}
              {sideArticles.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-6">
                  {sideArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} variant="grid" />
                  ))}
                  {sideArticles.length < 3 &&
                    Array(3 - sideArticles.length).fill(null).map((_, i) => (
                      <div key={`placeholder-${i}`} className="rounded-xl aspect-[4/3] bg-muted animate-pulse" />
                    ))
                  }
                </div>
              )}
            </div>

            {/* Sidebar droite : Vidéo + En Direct */}
            <div className="lg:col-span-4">
              <div className="sticky top-24 space-y-4">
                {/* Widget Vidéo */}
                <VideoWidget
                  video={featuredVideo}
                  isAdmin={isAdmin}
                  token={token}
                  onUpdate={(v) => setFeaturedVideo(v)}
                />

                {/* En Direct — compact */}
                <div className="bg-card border border-border rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <div className="relative">
                        <Radio className="h-4 w-4 text-[#D62828]" />
                        <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 bg-[#D62828] rounded-full animate-ping" />
                      </div>
                      <h2 className="font-serif font-bold text-base">En direct</h2>
                    </div>
                    <Link to="/live" className="flex items-center gap-1 text-[#D62828] hover:underline text-[11px] font-bold">
                      Voir le live
                      <ChevronRight className="h-3 w-3" />
                    </Link>
                  </div>

                  <div className="space-y-0.5">
                    {liveArticles.map((article) => (
                      <ArticleCardLive key={article.id} article={article} />
                    ))}
                  </div>
                </div>

                {/* Zap Chapeau — widget editorial (config admin only) */}
                {zapInSidebar && <ZapChapeauLauncher articles={articles} categories={categories} />}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Zap Chapeau — juste sous la Une (bloc éditorial pleine largeur) */}
      {zapAfterFeatured && (
        <section className="container mx-auto px-4 py-6" data-testid="zap-after-featured">
          <ZapChapeauLauncher articles={articles} categories={categories} />
        </section>
      )}

      {/* Ad: header placement */}
      <div className="container mx-auto px-4">
        <AdBlock placement="header" />
      </div>

      {isSectionVisible(cc, 'homepage_markets_widget') && <MarketsPulseWidget />}

      {/* Bloc Événements & Billetterie (s'auto-masque si aucun événement publié) */}
      {isSectionVisible(cc, 'homepage_events') && <HomeEventsBlock />}

      {/* Categories Tags */}
      <section className="border-b border-border bg-muted/30">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((cat) => (
              <Link key={cat.id} to={`/category/${cat.slug}`}>
                <Badge 
                  variant="outline" 
                  className="whitespace-nowrap px-4 py-2 text-sm hover:bg-[#D62828] hover:text-white hover:border-[#D62828] transition-colors cursor-pointer rounded-full"
                >
                  {cat.name}
                </Badge>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-[#D62828]" />
              <h2 className="font-serif font-bold text-2xl">Dernières actualités</h2>
            </div>
            <Button variant="ghost" asChild className="text-[#D62828] hover:text-[#D62828]">
              <Link to="/category/actualite" className="flex items-center gap-1">
                Voir tout <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {latestArticles.slice(0, 6).map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </div>
      </section>

      {/* InfoFlow Social — showcase premium dormant. Demo-only, aucune fonctionnalite active.
          Place apres "Dernieres actualites" et avant le PartnerBlock pour equilibre visuel.
          Masqué quand le module social est désactivé (features.social.enabled=false). */}
      {socialEnabled && <InfoFlowSocialShowcase />}

      {/* Bloc central — Partenaire (régie). Self-hiding si aucune campagne active.
          Placé entre Actualité et Politique pour équilibre visuel. */}
      <PartnerBlock />

      {/* Category Sections */}
      {categories.slice(0, 4).map((category) => {
        const categoryArticles = articles.filter(a => a.category_id === category.id);
        if (categoryArticles.length === 0) return null;

        return (
          <section key={category.id} className="py-12 border-t border-border">
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-serif font-bold text-2xl flex items-center gap-3">
                  <span 
                    className="w-1.5 h-8 rounded-full" 
                    style={{ backgroundColor: category.color }}
                  />
                  {category.name}
                </h2>
                <Button variant="ghost" asChild className="text-[#D62828] hover:text-[#D62828]">
                  <Link to={`/category/${category.slug}`} className="flex items-center gap-1">
                    Voir tout <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {categoryArticles.slice(0, 4).map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Newsletter CTA */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-serif font-bold text-3xl mb-4">
            Restez informé
          </h2>
          <p className="text-white/70 mb-6 max-w-md mx-auto">
            Recevez les dernières actualités directement dans votre boîte mail, 
            avec nos analyses exclusives.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="Votre email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-[#D62828]"
              data-testid="newsletter-email"
            />
            <Button className="bg-[#D62828] hover:bg-[#B91C1C] px-6 rounded-lg" data-testid="newsletter-submit"
              onClick={async () => {
                if (!newsletterEmail || !newsletterEmail.includes('@')) {
                  toast.error('Veuillez entrer une adresse email valide');
                  return;
                }
                try {
                  const res = await axios.post(`${API_URL}/api/newsletter/subscribe`, { email: newsletterEmail });
                  toast.success(res.data.message || 'Inscription réussie !');
                  setNewsletterEmail('');
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
                }
              }}>
              S'inscrire
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
